import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiUrl } from '../config';
import './ActivityBoard.css';

// Вспомогательный компонент для карточки активности
const ActivityCard = ({ activity, onViewUserProfile, telegramId, onDeleteActivity }) => {
    // Форматирование даты
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // Получаем данные о создателе
    const creator = activity.creator || {};
    
    // Определяем, является ли текущий пользователь автором активности
    const isOwnActivity = creator.telegram_id === telegramId;
    
    // Определяем URL аватара
    let avatarSrc = '/avatars/default-avatar.png'; // Дефолтный путь
    if (creator.avatar) {
        if (creator.avatar.startsWith('/')) {
            avatarSrc = creator.avatar; // Путь уже правильный
        } else if (creator.avatar.startsWith('avatars/')) {
            avatarSrc = '/' + creator.avatar; // Добавляем слеш
        }
    }
    
    // Расчет оставшегося времени
    const getTimeRemaining = () => {
        if (!activity.expires_at) return null;
        
        const now = new Date();
        const expiry = new Date(activity.expires_at);
        const diffMs = expiry - now;
        
        if (diffMs <= 0) return "Истекло";
        
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (diffDays > 0) {
            return `${diffDays} д. ${diffHours} ч.`;
        } else {
            const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            return `${diffHours} ч. ${diffMinutes} мин.`;
        }
    };
    
    const timeRemaining = getTimeRemaining();

    // Добавляем обработчик клика на кнопку "Откликнуться"
    const handleRespond = (e) => {
        e.stopPropagation();
        // Вызываем функцию onRespond, передавая ей данные активности
        if (typeof onViewUserProfile === 'function') {
            const activityData = {
                id: activity.id,
                title: activity.title,
                creator: activity.creator
            };
            window.dispatchEvent(new CustomEvent('respond-to-activity', { 
                detail: activityData 
            }));
        }
    };
    
    // Обработчик удаления активности
    const handleDelete = (e) => {
        e.stopPropagation();
        if (window.confirm(`Вы действительно хотите удалить активность "${activity.title}"?`)) {
            onDeleteActivity(activity.id);
        }
    };

    return (
        <div className={`activity-card ${isOwnActivity ? 'own-activity' : ''}`}>
            <div className="activity-header">
                <div 
                    className="creator-info"
                    onClick={(e) => {
                        e.stopPropagation();
                        onViewUserProfile(creator);
                    }}
                >
                    <div className="creator-avatar">
                        <img 
                            src={avatarSrc} 
                            alt={`${creator.name || creator.username || 'Пользователь'}`}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/avatars/default-avatar.png';
                            }}
                        />
                    </div>
                    <div className="creator-name">
                        {creator.name || creator.username || `Пользователь ${creator.telegram_id}`}
                    </div>
                </div>
                <div className="activity-dates">
                    <div className="created-date">
                        <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        {formatDate(activity.created_at)}
                    </div>
                    {activity.expires_at && (
                        <div className="expires-date">
                            <span className="remaining-label">Осталось:</span>
                            <span className="remaining-time">{timeRemaining}</span>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="activity-content">
                <h3 className="activity-title">
                    {isOwnActivity && <span className="own-activity-badge">Моя активность</span>}
                    {activity.title}
                </h3>
                <p className="activity-description">{activity.description}</p>
                <div className="activity-actions">
                    {!isOwnActivity ? (
                        <button className="button button-primary" onClick={handleRespond}>
                            Откликнуться
                        </button>
                    ) : (
                        <button className="button button-secondary" onClick={handleDelete}>
                            Удалить
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Основной компонент списка активностей
const ActivityBoard = ({ telegramId, onViewUserProfile, currentUserCity }) => {
    // Состояния
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [deleteStatus, setDeleteStatus] = useState({ loading: false, error: null, success: null });
    const [cityFilterActive, setCityFilterActive] = useState(false);
    
    // Ref для "бесконечной" прокрутки
    const loadMoreRef = useRef(null);
    
    // Функция для загрузки активностей
    const fetchActivities = useCallback(async (page = 1, isLoadMore = false) => {
        if (isLoadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        
        setError(null);
        
        try {
            // Формируем URL с параметрами пагинации и фильтрации по telegram_id
            let url = getApiUrl(`/api/activities/list?page=${page}&page_size=10${telegramId ? `&telegram_id=${telegramId}` : ''}`);
            
            // Добавляем параметр фильтрации по городу, если фильтр активен и город указан
            if (cityFilterActive && currentUserCity) {
                url += `&filter_city=true`;
            }
            
            // Выполняем запрос
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`Ошибка при загрузке активностей. Код: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Обновляем состояния
            if (isLoadMore) {
                setActivities(prev => [...prev, ...data.activities]);
            } else {
                setActivities(data.activities);
            }
            
            setCurrentPage(data.page);
            setHasMore(data.has_next);
            setTotalCount(data.total_count);
            
        } catch (err) {
            console.error('Ошибка при загрузке активностей:', err);
            setError(err.message);
        } finally {
            if (isLoadMore) {
                setLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    }, [telegramId, cityFilterActive, currentUserCity]);
    
    // Функция для удаления активности
    const handleDeleteActivity = useCallback(async (activityId) => {
        if (!telegramId || !activityId) {
            console.error('Не удалось удалить активность: отсутствует ID');
            return;
        }
        
        setDeleteStatus({ loading: true, error: null, success: null });
        
        try {
            const formData = new FormData();
            formData.append('telegram_id', telegramId);
            formData.append('activity_id', activityId);
            
            const response = await fetch(getApiUrl('/api/activities/delete'), {
                method: 'POST',
                body: formData
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Не удалось удалить активность');
            }
            
            // Обновляем список после успешного удаления
            setDeleteStatus({ loading: false, error: null, success: data.message });
            
            // Удаляем активность из списка локально
            setActivities(prevActivities => 
                prevActivities.filter(activity => activity.id !== activityId)
            );
            
            // Обновляем счетчик
            setTotalCount(prev => Math.max(0, prev - 1));
            
            // Показываем сообщение об успешном удалении
            setTimeout(() => {
                setDeleteStatus(prev => ({ ...prev, success: null }));
            }, 3000);
            
        } catch (err) {
            console.error('Ошибка при удалении активности:', err);
            setDeleteStatus({ loading: false, error: err.message, success: null });
            
            // Скрываем сообщение об ошибке через 3 секунды
            setTimeout(() => {
                setDeleteStatus(prev => ({ ...prev, error: null }));
            }, 3000);
        }
    }, [telegramId]);
    
    // Загрузка данных при монтировании компонента или изменении фильтра по городу
    useEffect(() => {
        fetchActivities(1, false);
    }, [fetchActivities, cityFilterActive]);
    
    // Наблюдение за прокруткой для загрузки дополнительных данных
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
                    fetchActivities(currentPage + 1, true);
                }
            },
            { threshold: 0.1 }
        );
        
        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }
        
        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [fetchActivities, hasMore, loadingMore, loading, currentPage]);
    
    // Рендер компонента
    return (
        <div className="activity-board">
            <div className="activity-header-bar">
                <h2>Активности</h2>
                <div className="activity-stats">
                    {totalCount > 0 && <span>{totalCount} актив.</span>}
                </div>
            </div>
            
            {/* Переключатель фильтра по городу */}
            {currentUserCity && (
                <div className="activity-filter-container">
                    <label className="activity-filter-toggle">
                        <input 
                            type="checkbox" 
                            checked={cityFilterActive} 
                            onChange={() => setCityFilterActive(!cityFilterActive)} 
                        />
                        <span className="activity-filter-slider"></span>
                        <span className="activity-filter-label">
                            {cityFilterActive ? `Только ${currentUserCity}` : 'Все города'}
                        </span>
                    </label>
                </div>
            )}
            
            {/* Показываем статусы удаления */}
            {deleteStatus.success && (
                <div className="activity-status success">
                    {deleteStatus.success}
                </div>
            )}
            
            {deleteStatus.error && (
                <div className="activity-status error">
                    Ошибка: {deleteStatus.error}
                </div>
            )}
            
            {loading && !loadingMore ? (
                <div className="activity-loading">
                    <div className="loading-spinner"></div>
                    <p>Загрузка активностей...</p>
                </div>
            ) : error ? (
                <div className="activity-error">
                    <p>Ошибка: {error}</p>
                    <button onClick={() => fetchActivities(1, false)} className="button button-primary">Попробовать снова</button>
                </div>
            ) : activities.length === 0 ? (
                <div className="activity-empty">
                    <p>Пока нет активных объявлений</p>
                </div>
            ) : (
                <>
                    <div className="activity-list">
                        {activities.map(activity => (
                            <ActivityCard 
                                key={activity.id} 
                                activity={activity}
                                telegramId={telegramId}
                                onViewUserProfile={onViewUserProfile}
                                onDeleteActivity={handleDeleteActivity}
                            />
                        ))}
                    </div>
                    
                    {hasMore && (
                        <div ref={loadMoreRef} className="load-more">
                            {loadingMore && (
                                <div className="loading-spinner"></div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ActivityBoard;